import { Cuboid } from '../entities/Cuboid';
import type { Frame } from '../entities/Frame';
import type { Position } from '../value-objects/Position';
import type { Dimensions } from '../value-objects/Dimensions';

export type CuboidTransitionState = 'matched' | 'entering' | 'exiting';

export interface CuboidTransition {
  readonly uuid: string;
  readonly state: CuboidTransitionState;
  readonly prev: Cuboid | null;
  readonly next: Cuboid | null;
}

function lerpNum(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpPosition(a: Position, b: Position, t: number): Position {
  return { x: lerpNum(a.x, b.x, t), y: lerpNum(a.y, b.y, t), z: lerpNum(a.z, b.z, t) };
}

function lerpDimensions(a: Dimensions, b: Dimensions, t: number): Dimensions {
  return {
    width: lerpNum(a.width, b.width, t),
    height: lerpNum(a.height, b.height, t),
    depth: lerpNum(a.depth, b.depth, t),
  };
}

function shortestAngleLerp(a: number, b: number, t: number): number {
  let delta = b - a;
  // Normalize to [-PI, PI]
  delta = delta - Math.PI * 2 * Math.round(delta / (Math.PI * 2));
  return a + delta * t;
}

export function interpolate(prev: Cuboid, next: Cuboid, t: number): Cuboid {
  if (prev.uuid !== next.uuid) {
    throw new Error(`Cannot interpolate cuboids with different UUIDs: ${prev.uuid} vs ${next.uuid}`);
  }
  return new Cuboid(
    next.uuid,
    lerpPosition(prev.position, next.position, t),
    lerpDimensions(prev.dimensions, next.dimensions, t),
    { yaw: shortestAngleLerp(prev.transform.yaw, next.transform.yaw, t) },
    next.info,
  );
}

export function resolveTransition(
  prevFrame: Frame | null,
  nextFrame: Frame,
): CuboidTransition[] {
  const transitions: CuboidTransition[] = [];
  const prevCuboids = prevFrame?.cuboids ?? new Map<string, Cuboid>();

  // Matched + entering
  for (const [uuid, next] of nextFrame.cuboids) {
    const prev = prevCuboids.get(uuid) ?? null;
    transitions.push({
      uuid,
      state: prev ? 'matched' : 'entering',
      prev,
      next,
    });
  }

  // Exiting
  for (const [uuid, prev] of prevCuboids) {
    if (!nextFrame.cuboids.has(uuid)) {
      transitions.push({
        uuid,
        state: 'exiting',
        prev,
        next: null,
      });
    }
  }

  return transitions;
}
